<template>
  <div v-if="hasLayerOptions">
    <v-select
      v-if="stylesItems.length > 1"
      :items="stylesItems"
      v-model="styles"
      :item-text="selectStyleText"
      :item-value="selectStyleValue"
      :label="$t('Pilot.Layer.Config.SelectStyle')"
    />
    <img class="legend" v-if="color == 'alg2'" :src="require('@/assets/scale-alg2.png')" />
    <img class="legend" v-if="color == 'redblue'" :src="require('@/assets/scale-redblue.png')" />
    <img class="legend" v-if="color == 'sst_36'" :src="require('@/assets/scale-sst_36.png')" />
    <img class="legend" v-if="styles.indexOf('rainbow') > -1" :src="require('@/assets/scale-alg2.png')" />
    <v-layout row wrap v-if="stylesItems.length > 0">
      <v-flex xs12 sm6>
        <v-text-field
          hide-details
          class="mr-1"
          v-model.number="min"
          type="number"
          :label="`Min [${value.colorscaleunit}]`"
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm6>
        <v-text-field
          hide-details
          class="ml-1"
          v-model.number="max"
          type="number"
          :label="`Max [${value.colorscaleunit}]`"
        ></v-text-field>
      </v-flex>
    </v-layout>
    <div v-for="option in options">
      <template v-if="option.key == 'time'">
        <v-menu
          :close-on-content-click="false"
          v-model="showDate"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          max-width="310px"
          min-width="310px"
        >
          <v-text-field
            slot="activator"
            v-model="dateFormated"
            :label="option.label"
            prepend-icon="mdi-calendar"
            readonly
          ></v-text-field>
          <v-date-picker
            :min="minDate"
            :max="maxDate"
            v-model="date"
            :allowed-dates="isAllowedDate"
            no-title
            @input="showDate = false"
          ></v-date-picker>
        </v-menu>
      </template>
      <v-select
        hide-details
        v-else
        :items="option.values"
        :label="option.label"
        :value="option.default"
        @change="updateOption(option.key, $event)"
      >
        <template #item="{ item }">
          {{ Math.round(item) }}
          <template v-if="option.units">{{ option.units }}</template>
        </template>
        <template #selection="{ item }">
          {{ Math.round(item) }}
          <template v-if="option.units">{{ option.units }}</template>
        </template>
      </v-select>
    </div>
  </div>
</template>

<script lang="ts">
import { LayerConfig } from '@/models/ui/LayerConfig'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { LayerSourceType } from '@/enum/LayerSourceType'
import { LayerDateStep } from '@/enum/LayerDateStep'

@Component({
  name: 'AdminConfigLayer',
  props: {
    value: {
      type: Object,
      default: () => new LayerConfig(),
    },
  },
})
export default class AdminConfigLayer extends Vue {
  value!: LayerConfig
  showDate = false

  get hasStyleOptions() {
    return this.stylesItems.length > 0
  }

  get hasLayerOptions() {
    return (
      (this.value.colorscalemin != null && this.value.colorscalemax != null) ||
      Object.keys(this.value.options ?? {}).some(k => this.value.options[k]?.enable)
    )
  }

  get stylesItems() {
    return this.value?.styles ?? []
  }

  get styles() {
    return this.value?.defaulStyle ?? this.selectStyleValue(this.stylesItems[0]) ?? null
  }

  set styles(newValue) {
    this.$emit('input', { ...this.value, defaulStyle: newValue })
  }

  get color() {
    return this.styles?.split('/')[1] ?? ''
  }

  get min() {
    return this.value.colorscalemin
  }

  set min(newValue) {
    this.$emit('input', { ...this.value, colorscalemin: newValue })
  }

  get max() {
    return this.value.colorscalemax
  }

  set max(newValue) {
    this.$emit('input', { ...this.value, colorscalemax: newValue })
  }

  get options() {
    return (
      Object.keys(this.value?.options ?? {})
        .map(key => ({ key, ...this.value.options[key] }))
        .filter((o: any) => o.enable) ?? []
    )
  }

  updateOption(key: string, newValue: any) {
    const newOptions = { ...this.value.options }
    newOptions[key].default = newValue
    this.$emit('input', { ...this.value, options: newOptions })
  }

  get startDate() {
    const optionTime = this.value.options['time']
    if (optionTime) {
      return new Date(optionTime.startDate)
    }
    return new Date()
  }

  get maxDate() {
    const optionTime = this.value.options['time']

    const offsetPicth = optionTime.step == LayerDateStep.HOUR ? 1000 * 60 * 60 : 1000 * 60 * 60 * 24

    let maxD = new Date()
    if (optionTime.step == LayerDateStep.MONTH) {
      maxD = new Date(maxD.getTime() + optionTime.endDateTodayOffset * offsetPicth)
    }
    maxD.setUTCHours(this.startDate.getUTCHours())
    maxD.setUTCMinutes(this.startDate.getUTCMinutes())
    maxD.setUTCSeconds(this.startDate.getUTCMinutes())
    maxD.setUTCMilliseconds(this.startDate.getUTCMilliseconds())
    if (optionTime.step == LayerDateStep.MONTH) {
      maxD.setUTCDate(this.startDate.getUTCDate())
      return maxD.toISOString().split('T')[0]
    }

    return new Date(maxD.getTime() + optionTime.endDateTodayOffset * offsetPicth).toISOString().split('T')[0]
  }

  get minDate() {
    return this.startDate.toISOString().split('T')[0]
  }

  get date() {
    const optionTime = this.value.options['time']
    return optionTime.default.split('T')[0]
  }

  set date(newValue) {
    const newDate = new Date(newValue)
    newDate.setUTCHours(this.startDate.getUTCHours())
    newDate.setUTCMinutes(this.startDate.getUTCMinutes())
    newDate.setUTCSeconds(this.startDate.getUTCMinutes())
    newDate.setUTCMilliseconds(this.startDate.getUTCMilliseconds())
    this.updateOption('time', newDate.toISOString())
  }

  get dateFormated() {
    const [year, month, day] = this.date.split('-')
    return `${day}/${month}/${year}`
  }

  selectStyleValue(it: any) {
    return it?.Name || it?.Identifier || it
  }

  selectStyleText(it: any) {
    return it?.Title || it?.Name || it?.Identifier || it
  }

  isAllowedDate(date: string) {
    const optionTime = this.value.options['time']
    return optionTime.step == LayerDateStep.MONTH ? new Date(date).getDate() == 1 : true
  }
}
</script>

<style scope>
.legend {
  width: 100%;
  max-height: 20px;
}
</style>
